// src/pages/CompanyReportDownlinePage.jsx
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetCompanyReportEventQuery } from '../services/sports/sportsApi';
import Loading from '../components/Loading';

const CompanyReportDownlinePage = () => {
  const navigate = useNavigate();
  const { eventId, matchName, userId } = useParams();

  // Fetch company report with userId for downline
  const { data: companyReportData, isLoading, error, isFetching } = useGetCompanyReportEventQuery({
    eventId: eventId,
    userId: userId
  });

  const reportData = companyReportData?.data || [];
  const summary = companyReportData?.summary || {};

  // Get dynamic title based on userType
  const getReportTitle = () => {
    if (reportData.length > 0) {
      const userType = reportData[0].userType;
      const userTypeMapping = {
        'SuperAdmin': 'Super Admin',
        'MiniAdmin': 'Mini Admin',
        'Master': 'Master',
        'Super': 'Super Master',
        'Agent': 'Agent',
        'Client': 'Client'
      };
      return `${userTypeMapping[userType] || userType} Company Report`;
    }
    return 'Company Report - Downline';
  };

  // Get parent level share column name based on current user type
  const getParentShareColumnName = () => {
    if (reportData.length > 0) {
      const userType = reportData[0].userType;
      const parentMapping = {
        'SuperAdmin': 'Admin Share',
        'MiniAdmin': 'Super Admin Share',
        'Master': 'Mini Admin Share',
        'Super': 'Master Share',
        'Agent': 'Agent Share',
        'Client': 'My Share'
      };
      return parentMapping[userType] || 'My Share';
    }
    return 'My Share';
  };

  // Handle user click to navigate to further downline
  const handleUserClick = (item) => {
    if (item.userId && item.userType !== 'Client') {
      navigate(`/sports-betting/company-report/${eventId}/${encodeURIComponent(matchName)}/downline/${item.userId}`);
    }
  };

  return (
    <div className="w-full">
      {/* Company Report Table */}
      <div className="bg-[#2c3e3e] text-white px-5 py-3 flex items-center justify-between">
        <div>
          <div className="text-xl font-normal">{getReportTitle()}</div>
        </div>
        <button 
          onClick={() => navigate(-1)}
          className="bg-blue-500 text-white px-6 py-2 hover:bg-blue-600"
        >
          Back
        </button>
      </div>

      {/* Show loading overlay when fetching new data */}
      {isFetching && !isLoading && (
        // <div className="bg-white bg-opacity-75 py-4 text-center">
          <Loading />
        // </div>
      )}

      <div className="overflow-x-auto mt-1 py-0">
        <table className="w-full border-collapse text-sm border border-gray-300">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="p-2 border border-gray-600 text-left">
                <div className="flex items-center">
                  <button className="mr-2">🔍</button>
                  Code
                </div>
              </th>
              <th className="p-2 border border-gray-600 text-left">Name</th>
              <th className="p-2 border border-gray-600 text-left">Match Amt</th>
              <th className="p-2 border border-gray-600 text-left">Sess Amt</th>
              <th className="p-2 border border-gray-600 text-left">Total</th>
              <th className="p-2 border border-gray-600 text-left">Match Comm+</th>
              <th className="p-2 border border-gray-600 text-left">Sess Comm+</th>
              <th className="p-2 border border-gray-600 text-left">Total Comm</th>
              <th className="p-2 border border-gray-600 text-left">Total Amount</th>
              <th className="p-2 border border-gray-600 text-left">{getParentShareColumnName()}</th>
              <th className="p-2 border border-gray-600 text-left">M.App</th>
              <th className="p-2 border border-gray-600 text-left">Net Amount</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="12" className="p-8 text-center">
                  <Loading />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="12" className="p-4 text-red-500 text-center">
                  {error.message || "Failed to load data"}
                </td>
              </tr>
            ) : reportData.length === 0 ? (
              <tr>
                <td colSpan="12" className="p-4 text-center text-gray-400">
                  No Data
                </td>
              </tr>
            ) : (
              <>
                {reportData.map((item, index) => (
                  <tr key={index} className="bg-white">
                    <td className="p-2 border border-gray-300">
                      {item.userId && item.userType !== 'Client' ? (
                        <button
                          onClick={() => handleUserClick(item)}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        >
                          {item.code}
                        </button>
                      ) : (
                        <span>{item.code}</span>
                      )}
                    </td>
                    <td className="p-2 border border-gray-300">{item.name}</td>
                    <td className={`p-2 border border-gray-300 ${item.matchAmt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.matchAmt?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.sessAmt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.sessAmt?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.total?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.matchComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.matchComm?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.sessComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.sessComm?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.totalComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.totalComm?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.totalAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.totalAmount?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.myCompanyShare >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.myShare?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.mApp >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.mApp?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.netAmount >= 0 ? 'text-green-600' : 'text-red-600'} font-semibold`}>
                      {item.netAmount?.toFixed(2)}
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-[#d4e157]">
                  <td colSpan="2" className="p-2 border border-gray-300 font-semibold">Total</td>
                  <td className={`p-2 border border-gray-300 ${summary.matchAmt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.matchAmt?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.sessAmt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.sessAmt?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.total?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.matchComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.matchComm?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.sessComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.sessComm?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.totalComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.totalComm?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.totalAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.totalAmount?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.myCompanyShare >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.myShare?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.mApp >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.mApp?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.netAmount >= 0 ? 'text-green-600' : 'text-red-600'} font-semibold`}>
                    {summary.netAmount?.toFixed(2)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyReportDownlinePage;
